import { BaseHttpClient, HttpClientResponse } from '~/passporter-services/baseRepository'
import CookieService from '~/services/cookies'
import Settings from '~/services/settings'
import { TranslationService } from '~/services/Translation'

export class HttpClient implements BaseHttpClient {
	private static instance: HttpClient

	private constructor() {}

	public static getInstance(): HttpClient {
		if (!this.instance) this.instance = new HttpClient()
		return this.instance
	}

	public async options<T>(
		url: string,
		params?: Record<string, any>,
		headers?: Record<string, string>
	): Promise<HttpClientResponse<T>> {
		const request = this.buildRequest('OPTIONS', headers)
		const fullUrl = this.buildUrl(url, params)

		const response = await fetch(fullUrl, request)

		return await this.manageResponse<T>(response)
	}

	public async get<T>(
		url: string,
		params?: Record<string, any>,
		headers?: Record<string, string>
	): Promise<HttpClientResponse<T>> {
		const request = this.buildRequest('GET', headers)
		const fullUrl = this.buildUrl(url, params)

		const response = await fetch(fullUrl, request)

		return await this.manageResponse<T>(response)
	}

	public async post<T>(
		url: string,
		body: any,
		params?: Record<string, any>,
		headers?: Record<string, string>
	): Promise<HttpClientResponse<T>> {
		const request = this.buildRequest('POST', headers, body)
		const fullUrl = this.buildUrl(url, params)

		const response = await fetch(fullUrl, request)

		return await this.manageResponse<T>(response)
	}

	public async patch<T>(
		url: string,
		body: any,
		params?: Record<string, any>,
		headers?: Record<string, string>
	): Promise<HttpClientResponse<T>> {
		const request = this.buildRequest('PATCH', headers, body)
		const fullUrl = this.buildUrl(url, params)

		const response = await fetch(fullUrl, request)

		return await this.manageResponse<T>(response)
	}

	public async delete<T>(
		url: string,
		headers?: Record<string, string>
	): Promise<HttpClientResponse<T>> {
		const request = this.buildRequest('DELETE', headers)
		const fullUrl = this.buildUrl(url)

		const response = await fetch(fullUrl, request)

		return await this.manageResponse<T>(response)
	}

	private buildRequest(
		method: string,
		headers?: Record<string, string>,
		body?: BodyInit
	): RequestInit {
		const token = this.getToken()
		const theHeaders: HeadersInit = {
			'Accept-Language': this.getLanguage(),
			...headers,
		}
		if (token) theHeaders['Authorization'] = `Bearer ${this.getToken()}`
		if (typeof body === 'object' && !(body instanceof FormData)) {
			theHeaders['Content-type'] = 'application/json'
			body = JSON.stringify(body)
		}

		return {
			method,
			body,
			headers: theHeaders,
		}
	}

	private buildUrl(url: string, params?: Record<string, any>): string {
		let fullUrl: string = `${this.getBaseUrl()}/${url}`
		if (params) {
			const urlParams = this.buildParams(params)
			fullUrl = `${fullUrl}?${urlParams}`
		}

		return fullUrl
	}

	private buildParams(params: Record<string, any>) {
		const urlParams = new URLSearchParams()
		Object.entries(params).forEach(([key, value]) => {
			if (Array.isArray(value)) {
				value.forEach((item) => {
					if (item === undefined || item === null) return
					urlParams.append(key, String(item))
				})
				return
			}
			if (value === undefined || value === null) return
			urlParams.append(key, String(value))
		})
		return urlParams
	}

	private async manageResponse<T>(response: Response): Promise<HttpClientResponse<T>> {
		const theResponse: HttpClientResponse<T> = { status: response.status }
		try {
			const noContent: boolean = response.status === 204
			if (noContent) return theResponse
			const data = await response.json()
			theResponse.data = data
		} catch {
			console.error('fail passporter response')
		}
		return theResponse
	}

	private getLanguage(): string {
		return TranslationService.getInstance().getLocale()
	}

	private getToken(): string | undefined {
		const cookie = CookieService.getCookie('authentication-cookie')
		return cookie?.auth?.accessToken
	}

	private getBaseUrl(): string {
		return Settings.retrieveApiUrl()
	}
}
